import type { PanelLocale } from "./types";

export type Dict = Record<string, string>;

const en: Dict = {
  "nav.overview": "Dashboard",
  "nav.accounts": "Accounts",
  "nav.sites": "Domains",
  "nav.files": "File manager",
  "nav.apps": "Apps",
  "nav.settings": "Appearance",
  "nav.plugins": "Plugins",
  "nav.git": "Git deploy",
  "login.title": "Sign in",
  "login.username": "Username",
  "login.password": "Password",
  "login.submit": "Sign in",
  "theme.dark": "Dark",
  "theme.light": "Premium light",
  "theme.midnight": "Midnight",
  "theme.system": "System",
  "polish.title": "Appearance & language",
  "polish.theme": "Theme",
  "polish.language": "Language",
  "plugins.title": "Plugins",
  "git.title": "Git deploy",
  "mass.suspend": "Suspend selected",
  "mass.unsuspend": "Unsuspend selected",
  "common.refresh": "Refresh",
  "common.save": "Save"
};

const es: Dict = {
  ...en,
  "nav.overview": "Panel",
  "nav.accounts": "Cuentas",
  "nav.sites": "Dominios",
  "nav.files": "Archivos",
  "nav.apps": "Aplicaciones",
  "nav.settings": "Apariencia",
  "nav.plugins": "Plugins",
  "nav.git": "Despliegue Git",
  "login.title": "Iniciar sesión",
  "login.username": "Usuario",
  "login.password": "Contraseña",
  "login.submit": "Entrar",
  "theme.dark": "Oscuro",
  "theme.light": "Claro premium",
  "theme.midnight": "Medianoche",
  "theme.system": "Sistema",
  "polish.title": "Apariencia e idioma",
  "polish.theme": "Tema",
  "polish.language": "Idioma",
  "plugins.title": "Plugins",
  "git.title": "Despliegue Git",
  "mass.suspend": "Suspender seleccionados",
  "mass.unsuspend": "Reactivar seleccionados",
  "common.refresh": "Actualizar",
  "common.save": "Guardar"
};

const ar: Dict = {
  ...en,
  "nav.overview": "لوحة التحكم",
  "nav.accounts": "الحسابات",
  "nav.sites": "النطاقات",
  "nav.files": "مدير الملفات",
  "nav.apps": "التطبيقات",
  "nav.settings": "المظهر",
  "nav.plugins": "الإضافات",
  "nav.git": "نشر Git",
  "login.title": "تسجيل الدخول",
  "login.username": "اسم المستخدم",
  "login.password": "كلمة المرور",
  "login.submit": "دخول",
  "theme.dark": "داكن",
  "theme.light": "فاتح",
  "theme.midnight": "منتصف الليل",
  "theme.system": "النظام",
  "polish.title": "المظهر واللغة",
  "polish.theme": "السمة",
  "polish.language": "اللغة",
  "plugins.title": "الإضافات",
  "git.title": "نشر Git",
  "mass.suspend": "تعليق المحدد",
  "mass.unsuspend": "إلغاء تعليق المحدد",
  "common.refresh": "تحديث",
  "common.save": "حفظ"
};

const PACKS: Record<PanelLocale, Dict> = { en, es, ar };

const LOCALE_KEY = "realpanel_locale";
/** v2 defaults to premium light SaaS theme */
const THEME_KEY = "realpanel_theme_v2";

export function getLocale(): PanelLocale {
  const v = localStorage.getItem(LOCALE_KEY);
  if (v === "es" || v === "ar" || v === "en") return v;
  return "en";
}

export function setLocale(locale: PanelLocale) {
  localStorage.setItem(LOCALE_KEY, locale);
  document.documentElement.lang = locale;
  document.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
}

export function t(key: string, locale?: PanelLocale): string {
  const loc = locale || getLocale();
  return PACKS[loc][key] || PACKS.en[key] || key;
}

export type ThemeId = "dark" | "light" | "midnight" | "system";

export function getTheme(): ThemeId {
  const v = localStorage.getItem(THEME_KEY);
  if (v === "dark" || v === "light" || v === "midnight" || v === "system") {
    return v;
  }
  // Premium SaaS default — light
  return "light";
}

export function resolveTheme(theme: ThemeId): "dark" | "light" | "midnight" {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  }
  return theme;
}

export function applyTheme(theme: ThemeId) {
  localStorage.setItem(THEME_KEY, theme);
  const resolved = resolveTheme(theme);
  document.documentElement.setAttribute("data-theme", resolved);
  document.documentElement.classList.toggle("theme-light", resolved === "light");
  document.documentElement.classList.toggle("theme-dark", resolved !== "light");
}

export function initAppearance() {
  setLocale(getLocale());
  applyTheme(getTheme());
}
